import React, { useState, useEffect, useMemo } from "react";
import Sidebar from "../../component/Sidebar";
import Topbar from "../../component/Topbar";
import { FaBoxOpen, FaFilter } from "react-icons/fa";
import axios from "axios";
import { baseurl } from "../../baseurl";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

function ProductPerformance() {
  const [showFilter, setShowFilter] = useState(false);
  const [filters, setFilters] = useState({ search: "" });
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  const vendorId = localStorage.getItem("vendorId");

  // Fetch product performance
  useEffect(() => {
    const fetchPerformance = async () => {
      try {
        setLoading(true);
        const res = await axios.get(`${baseurl}getVendorProductPerformance/${vendorId}`);
        if (res.data.success) {
          setProducts(res.data.productPerformance || []);
        } else {
          setProducts([]);
        }
      } catch (err) {
        console.error("Error fetching product performance:", err);
        setProducts([]);
      } finally {
        setLoading(false);
      }
    };
    if (vendorId) fetchPerformance();
  }, [vendorId]);

  // Filter logic
  const filteredProducts = useMemo(() => {
    return products.filter((p) =>
      `${p.name} ${p.sku}`.toLowerCase().includes(filters.search.toLowerCase())
    );
  }, [products, filters.search]);

  // Chart Data
  // Chart Data
const chartData = useMemo(() => {
  return {
    labels: filteredProducts.map((p) => p.name), // full names for tooltip
    datasets: [
      {
        label: "Total Revenue (₹)",
        data: filteredProducts.map((p) => p.total_revenue),
        backgroundColor: "rgba(75, 192, 192, 0.6)",
      },
      {
        label: "Quantity Sold",
        data: filteredProducts.map((p) => p.quantity_sold),
        backgroundColor: "rgba(54, 162, 235, 0.6)",
      },
    ],
  };
}, [filteredProducts]);

const chartOptions = {
  responsive: true,
  plugins: {
    legend: { position: "top" },
    title: { display: true, text: "Product Performance Overview" },
    tooltip: {
      callbacks: {
        label: function (tooltipItem) {
          const p = filteredProducts[tooltipItem.dataIndex];
          const datasetLabel = tooltipItem.dataset.label;
          const value = tooltipItem.raw;
          return `${datasetLabel}: ${value} (${p.name})`;
        },
      },
    },
  },
  scales: {
    x: {
      ticks: {
        callback: function (val, index) {
          const name = filteredProducts[index].name;
          // Shorten name to 15 chars on axis
          return name.length > 15 ? name.substring(0, 15) + "..." : name;
        },
      },
    },
  },
};


  // Summary stats
  const totalRevenue = filteredProducts.reduce((acc, p) => acc + p.total_revenue, 0);
  const totalQuantity = filteredProducts.reduce((acc, p) => acc + p.quantity_sold, 0);

  return (
    <>
      <Topbar />
      <Sidebar />

      <main className="main-wrapper">
        <div className="main-content">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h4 className="breadcrumb-title d-flex align-items-center gap-2">
              <FaBoxOpen /> Product Performance
            </h4>
            <button
              className="btn btn-primary d-flex align-items-center gap-2"
              onClick={() => setShowFilter(!showFilter)}
            >
              <FaFilter /> Filters
            </button>
          </div>

          {/* Filters */}
          {showFilter && (
            <div className="card p-3 mb-3 shadow-sm border-0">
              <div className="row g-2 align-items-end">
                <div className="col-md-6">
                  <label>Search Product</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Product name or SKU"
                    value={filters.search}
                    onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                  />
                </div>
                <div className="col-md-3">
                  <button className="btn btn-primary w-100" onClick={() => setShowFilter(false)}>
                    Apply
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Stats Cards */}
          <div className="d-flex gap-3 mb-3">
            <div className="card p-3 flex-fill text-center shadow-sm border-0">
              <h6>Total Revenue</h6>
              <h4>₹{totalRevenue}</h4>
            </div>
            <div className="card p-3 flex-fill text-center shadow-sm border-0">
              <h6>Total Quantity Sold</h6>
              <h4>{totalQuantity}</h4>
            </div>
          </div>

          {/* Chart */}
          <div className="card shadow-sm border-0 mb-3 p-3">
            <Bar data={chartData} options={chartOptions} />
          </div>

          {/* Product Performance Table */}
          <div className="card shadow-sm border-0">
            <div className="card-body table-responsive">
              {loading ? (
                <div className="text-center py-4 text-muted">Loading product performance...</div>
              ) : filteredProducts.length > 0 ? (
                <table className="table table-hover align-middle">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Product</th>
                      <th>SKU</th>
                      <th>Quantity Sold</th>
                      <th>Total Revenue</th>
                      <th>% of Total Quantity</th>
                      <th>% of Total Revenue</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredProducts.map((p, i) => (
                      <tr key={p.product_id}>
                        <td>{i + 1}</td>
                        <td className="fw-semibold">{p.name}</td>
                        <td>{p.sku}</td>
                        <td>{p.quantity_sold}</td>
                        <td>₹{p.total_revenue}</td>
                        <td>{p.percent_quantity}%</td>
                        <td>{p.percent_revenue}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className="text-center text-muted py-4">No product performance data available</div>
              )}
            </div>
          </div>
        </div>
      </main>

      <style jsx>{`
        .table td,
        .table th {
          vertical-align: middle;
        }
      `}</style>
    </>
  );
}

export default ProductPerformance;
